import type { MetaDescriptor } from "react-router";

import { t } from "i18next";

import { createBaanaPublisher } from "@/src/utils/seo/builders";
import { toCanonicalUrl } from "@/src/utils/siteUrl";
import {
  WE_BUY_CARS_PATH,
  normalizePathname
} from "@/src/utils/weBuyCarsLandingPages";

const OG_IMAGE =
  "https://baana.nettiauto.com/assets/images/baana-by-nettiauto-logo-red.png";

interface WeBuyCarsMetaInput {
  pathname: string;
  title: string;
  description: string;
  // City or maker name; omitted on the generic /ostetaan-autoja page.
  landingName?: string;
  // City pages are served in that city; maker and generic pages cover Finland.
  kind?: "city" | "maker";
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

const buildBreadcrumbs = ({
  pathname,
  landingName
}: WeBuyCarsMetaInput): BreadcrumbItem[] => [
  { name: t("consumer.pageTitle.home"), path: "/" },
  { name: t("components.footer.weBuyCars"), path: WE_BUY_CARS_PATH },
  ...(landingName
    ? [
        {
          name: t("components.footer.weBuyCarsLink", { name: landingName }),
          path: pathname
        }
      ]
    : [])
];

const buildStructuredData = (input: WeBuyCarsMetaInput) => {
  const canonicalUrl = toCanonicalUrl(input.pathname);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: buildBreadcrumbs(input).map((crumb, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: crumb.name,
          item: toCanonicalUrl(crumb.path)
        }))
      },
      {
        "@type": "Service",
        name: input.title,
        description: input.description,
        url: canonicalUrl,
        serviceType: t("components.footer.weBuyCars"),
        provider: createBaanaPublisher(),
        areaServed:
          input.kind === "city" && input.landingName
            ? { "@type": "City", name: input.landingName }
            : { "@type": "Country", name: "Suomi" },
        inLanguage: "fi"
      }
    ]
  };
};

export const buildWeBuyCarsMeta = (
  rawInput: WeBuyCarsMetaInput
): MetaDescriptor[] => {
  const input = {
    ...rawInput,
    pathname: normalizePathname(rawInput.pathname)
  };
  const canonicalUrl = toCanonicalUrl(input.pathname);

  return [
    { title: input.title },
    { name: "description", content: input.description },
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:image", content: OG_IMAGE },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonicalUrl },
    { tagName: "link", rel: "canonical", href: canonicalUrl },
    { "script:ld+json": buildStructuredData(input) }
  ];
};
